/**
 * Error tracking in the browser.
 *
 * `@sentry/astro` looks for this file by name in the Astro project root and
 * injects an import of it into every page, ahead of anything else. It is not
 * reachable from `src/`, which is why the logic worth testing lives in
 * `src/libs/error-tracking.ts` and this file only wires it up.
 *
 * The DSN decides which service receives these events — Sentry or Better
 * Stack, per stage, from infra/error-tracking-provider.ts. Nothing below
 * changes between the two: Better Stack's error tracking is a Sentry-protocol
 * endpoint.
 */

import { breadcrumbsIntegration, init } from "@sentry/astro";

import {
  errorTrackingSettingsFromEnv,
  scrubEvent,
} from "~/libs/error-tracking";

const settings = errorTrackingSettingsFromEnv();

// No DSN means no SDK. Calling `init` without one is a no-op that still ships
// the integrations, their listeners and their bundle weight to every visitor.
if (settings) {
  init({
    ...settings,
    beforeSend: scrubEvent,
    // Added to the SDK's defaults, not instead of them: an array is merged as
    // `[...defaults, ...yours]` and then deduplicated by name, with the
    // later instance winning. So this does not turn breadcrumbs on — they are
    // on already — it replaces the default Breadcrumbs instance with one that
    // leaves the console alone.
    //
    // Breadcrumbs are what the browser did before it broke: clicks,
    // navigations, fetches. The console is deliberately out. A `console.error`
    // this site writes on purpose is the same event reported twice, and the
    // arguments ride along verbatim — which on this site is how a form's
    // contents would end up in a bug report nobody meant to put them in.
    //
    // Nothing else is removed here because nothing else needs to be: tracing,
    // session replay and profiling are not default integrations, so leaving
    // them unmentioned is enough to leave them out. The server has to remove
    // two by name — see sentry.server.config.ts, where the defaults differ.
    integrations: [breadcrumbsIntegration({ console: false })],
    // The counterpart to the cookie banner. Session Replay is not loaded, the
    // IP address is not attached, and no request body leaves the page — so
    // this runs on legitimate interest, with no cookie of its own, rather than
    // waiting for the analytics consent that gates PostHog. An error nobody
    // consented to report is an error nobody hears about.
    sendDefaultPii: false,
  });
}
